import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, RefreshCw, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (n) => new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

export default function DuplicatePaymentsCard({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/duplicate-payments?lookback_days=90`, { headers: headers() });
      setData(d);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'No se pudo revisar duplicados' });
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  useEffect(() => { load(); }, [load]);

  const orderGroups = data?.groups || [];
  const ledgerPending = data?.ledger_uncompensated || [];
  const unsupported = data && data.supported === false;
  const clean = data && !unsupported && !orderGroups.length && !ledgerPending.length;

  return (
    <div
      className={`rounded-xl border p-4 ${
        unsupported ? 'border-amber-200 bg-amber-50'
          : clean ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
      }`}
      data-testid="duplicate-payments-card"
    >
      <div className="flex items-center gap-2">
        {clean ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        ) : (
          <ShieldAlert className={`h-4 w-4 ${unsupported ? 'text-amber-700' : 'text-red-700'}`} />
        )}
        <span className={`text-sm font-semibold ${
          unsupported ? 'text-amber-800' : clean ? 'text-emerald-800' : 'text-red-800'
        }`}>
          {unsupported
            ? 'Revisión de duplicados no disponible en este entorno (requiere conexión directa a la base de datos)'
            : clean
              ? 'Sin dobles validaciones: cada crédito bancario está cobrado por una sola orden'
              : `${orderGroups.length + ledgerPending.length} movimiento(s) bancarios cobrados más de una vez`}
        </span>
        <button
          onClick={load} disabled={loading} data-testid="duplicate-payments-refresh"
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Revisar
        </button>
      </div>

      {!clean && !unsupported && data ? (
        <ul className="mt-3 space-y-1.5" data-testid="duplicate-payments-list">
          {orderGroups.map((g) => (
            <li key={`o-${g.bank_movement}`} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm">
              <span className="font-mono font-semibold">{g.bank_movement}</span>
              <span className="ml-2">{g.company_name}</span>
              <span className="ml-2 text-gray-600">órdenes {g.orders.join(', ')}</span>
              <span className="ml-2 font-semibold text-red-700">+{fmt(g.inflated_amount)} VES de más</span>
            </li>
          ))}
          {ledgerPending.map((g) => (
            <li key={`l-${g.bank_movement}`} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm">
              <span className="font-mono font-semibold">{g.bank_movement}</span>
              <span className="ml-2 text-gray-600">
                acreditado {g.count} veces en el ledger ({g.orders.join(', ') || 'sin orden'})
              </span>
              <span className="ml-2 font-semibold text-amber-700">sin compensar {fmt(g.net_inflation)} VES</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
